import type { CommandView } from '../../../shared/model/commandView'
import type { ProductOperation, ProductOperationResult } from './types'

/**
 * Итог операции словами. Исходы «200, но не записано» (регистр не опубликован,
 * пометка не публикуется) показываются как «не выполнено», а не как успех:
 * человек должен знать, что в 1С ничего не изменилось.
 */
const BARCODE_OUTCOMES: Readonly<Record<string, { text: string; done: boolean }>> = {
  Added: { text: 'Штрихкод добавлен', done: true },
  AlreadyExists: { text: 'Такой штрихкод у товара уже есть', done: true },
  Deleted: { text: 'Штрихкод удалён', done: true },
  MainSet: { text: 'Штрихкод сделан основным', done: true },
  Unchanged: { text: 'Штрихкод уже был основным', done: true },
  Invalid: { text: 'Штрихкод не прошёл проверку — в 1С ничего не записано', done: false },
  SkippedNotPublished: { text: 'Регистр штрихкодов не опубликован в 1С — запись не выполнена', done: false },
}

function resultText(result: ProductOperationResult): { text: string; done: boolean } {
  switch (result.kind) {
    case 'created':
      return result.outcome === 'Created'
        ? { text: 'Товар создан в 1С', done: true }
        : { text: 'Такой товар уже есть в этой базе — новый не создавался', done: true }
    case 'updated':
      return result.changed
        ? { text: 'Сохранено', done: true }
        : { text: 'Изменений не было: значения уже такие', done: true }
    case 'deleted':
      return result.deleted
        ? { text: 'Товар помечен на удаление', done: true }
        : { text: 'Пометка удаления не опубликована в 1С — товар не помечен', done: false }
    case 'refreshed':
      return {
        text: `Каталог обновлён из 1С: прочитано ${result.read}, новых ${result.created}, изменено ${result.updated}, пропущено ${result.skipped}`,
        done: true,
      }
    case 'barcode': {
      const known = BARCODE_OUTCOMES[result.outcome] ?? { text: `Итог: ${result.outcome}`, done: true }
      return result.message === null ? known : { ...known, text: `${known.text}. ${result.message}` }
    }
  }
}

function labelOf(operation: ProductOperation): string {
  const code = operation.payload.kind === 'barcode' ? operation.payload.barcode : ''
  switch (operation.action) {
    case 'create':
      return 'Создание товара'
    case 'update':
      return 'Сохранение'
    case 'delete':
      return 'Пометка на удаление'
    case 'barcode-add':
      return `Добавление штрихкода ${code}`
    case 'barcode-delete':
      return `Удаление штрихкода ${code}`
    case 'barcode-main':
      return `Основной штрихкод ${code}`
    case 'catalog-refresh':
      return 'Обновление каталога из 1С'
  }
}

export function operationView(operation: ProductOperation): CommandView & { key: string } {
  const base = { key: operation.key, label: labelOf(operation), error: operation.error, resendable: operation.resendable }
  if (operation.status === 'succeeded' && operation.result !== null) {
    const { text, done } = resultText(operation.result)
    return { ...base, status: done ? 'succeeded' : 'failed', message: text, resendable: false }
  }
  // Текст службы/1С — дословно (ADR-8).
  return { ...base, status: operation.status, message: operation.error?.message ?? null }
}
